import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Send, Bot, User, Loader2 } from 'lucide-react';
import { api } from '@/App';
import { useAuth, useTheme } from '@/App';
import { chatMarkdown } from '@/utils/chatMarkdown';

const INITIAL_MESSAGES = [{
  id: 1,
  role: 'bot',
  content: '¡Hola! Soy tu asistente de Aorra. Puedes consultar tus finanzas o pedirme ayuda paso a paso para usar cualquier función del portal. ¿Qué quieres hacer?'
}];

const FloatingAIChat = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState(INITIAL_MESSAGES);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const requestControllerRef = useRef(null);
  const chatSessionVersionRef = useRef(0);
  const isAuthenticatedRef = useRef(false);
  const { darkMode } = useTheme();
  const { user } = useAuth();

  // The chat belongs to the dashboard session. Closing that session must also
  // close the widget, discard its local history and stop any pending reply.
  useEffect(() => {
    chatSessionVersionRef.current += 1;
    isAuthenticatedRef.current = Boolean(user);

    if (!user) {
      requestControllerRef.current?.abort();
      requestControllerRef.current = null;
      setIsOpen(false);
      setIsLoading(false);
      setInputValue('');
      setMessages(INITIAL_MESSAGES);
    }

    return () => requestControllerRef.current?.abort();
  }, [user]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    if (isOpen) {
      scrollToBottom();
    }
  }, [messages, isOpen]);

  const handleSend = async () => {
    if (!inputValue.trim() || isLoading) return;
    if (!isAuthenticatedRef.current) {
      setIsOpen(false);
      return;
    }

    const userMessage = {
      id: Date.now(),
      role: 'user',
      content: inputValue.trim()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);
    const sessionVersion = chatSessionVersionRef.current;
    const requestController = new AbortController();
    requestControllerRef.current = requestController;

    try {
      const response = await api.post('/chat', {
        message: userMessage.content,
        // Enviar historial corto para dar contexto, opcional.
        // Pero la IA actuará solo con el prompt del usuario.
        history: messages.map(m => ({ role: m.role, content: m.content })).slice(-5)
      }, { signal: requestController.signal });

      // Do not render a reply that completed after the user logged out.
      if (!isAuthenticatedRef.current || sessionVersion !== chatSessionVersionRef.current) return;
      
      const botMessage = {
        id: Date.now() + 1,
        role: 'bot',
        content: response.data.reply
      };
      
      setMessages(prev => [...prev, botMessage]);
      if (response.data.accounts_changed) window.dispatchEvent(new Event("aorra:accounts-changed"));
    } catch (error) {
      const requestWasCancelled = error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError';
      if (requestWasCancelled || !isAuthenticatedRef.current || sessionVersion !== chatSessionVersionRef.current) return;
      console.error('Error in chat:', error);
      const errorMessage = {
        id: Date.now() + 1,
        role: 'bot',
        content: 'No pude procesar tu consulta en este momento. Por favor intenta nuevamente.'
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      if (sessionVersion === chatSessionVersionRef.current) {
        setIsLoading(false);
        if (requestControllerRef.current === requestController) requestControllerRef.current = null;
      }
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Helper para renderizar Markdown basico.
  // Como no queremos añadir dependencias complejas si no es necesario,
  // reemplazamos bold, saltos de linea y listas simples manualmente, 
  // o podemos usar un renderizado de html seguro, pero para evitar XSS, haremos reemplazo simple.
  const renderMarkdown = (text) => {
    if (!text) return null;
    
    return <div dangerouslySetInnerHTML={{ __html: chatMarkdown(text) }} className="markdown-body text-sm leading-relaxed" />;
  };

  return (
    <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end">
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className={`mb-4 w-80 sm:w-96 rounded-2xl shadow-2xl border flex flex-col overflow-hidden backdrop-blur-xl ${
              darkMode 
                ? 'bg-slate-900/85 border-slate-700 text-white shadow-cyan-900/20' 
                : 'bg-white/90 border-slate-200 text-slate-800 shadow-cyan-900/10'
            }`}
            style={{ height: '500px', maxHeight: '75vh' }}
          >
            {/* Header */}
            <div className={`p-4 flex items-center justify-between border-b ${
              darkMode ? 'border-slate-700/50 bg-slate-800/50' : 'border-slate-100 bg-slate-50/50'
            }`}>
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-full flex items-center justify-center overflow-hidden shrink-0">
                  <img src="/aorra-chat-flotante.png" alt="Aorra Bot" className="w-full h-full object-cover" />
                </div>
                <div>
                  <h3 className="font-bold text-sm font-outfit">Aorra Bot</h3>
                  <div className="flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
                    <span className="text-xs text-slate-500 dark:text-slate-400">En línea</span>
                  </div>
                </div>
              </div>
              <button 
                type="button"
                aria-label="Cerrar chat"
                onClick={() => setIsOpen(false)}
                className="p-2 rounded-full hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
              >
                <X className="w-5 h-5 text-slate-500 dark:text-slate-400" />
              </button>
            </div>

            {/* Chat Area */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {messages.map((msg) => (
                <div 
                  key={msg.id} 
                  className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div className={`max-w-[85%] rounded-2xl p-3 ${
                    msg.role === 'user' 
                      ? 'bg-cyan-600 text-white rounded-tr-sm' 
                      : darkMode
                        ? 'bg-slate-800 text-slate-200 rounded-tl-sm border border-slate-700/50'
                        : 'bg-white text-slate-700 rounded-tl-sm border border-slate-200 shadow-sm'
                  }`}>
                    {msg.role === 'user' ? (
                      <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                    ) : (
                      renderMarkdown(msg.content)
                    )}
                  </div>
                </div>
              ))}
              
              {isLoading && (
                <div className="flex justify-start">
                  <div className={`max-w-[80%] rounded-2xl p-3 rounded-tl-sm flex items-center gap-2 ${
                    darkMode ? 'bg-slate-800 border border-slate-700/50' : 'bg-white border border-slate-200 shadow-sm'
                  }`}>
                    <Loader2 className="w-4 h-4 text-cyan-600 animate-spin" />
                    <span className="text-xs text-slate-500 dark:text-slate-400 font-medium">Analizando tus finanzas...</span>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input Area */}
            <div className={`p-3 border-t ${darkMode ? 'border-slate-700/50 bg-slate-800/30' : 'border-slate-100 bg-white/50'}`}>
              <div className="relative flex items-center">
                <textarea
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Pregunta sobre tus gastos, metas..."
                  className={`w-full pr-12 py-3 pl-4 rounded-full resize-none h-11 flex items-center text-sm outline-none transition-all ${
                    darkMode 
                      ? 'bg-slate-900 border-slate-700 text-white placeholder:text-slate-500 focus:border-cyan-600 focus:ring-1 focus:ring-cyan-600' 
                      : 'bg-slate-100 border-transparent text-slate-900 focus:bg-white focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500'
                  } border`}
                  rows={1}
                />
                <button
                  type="button"
                  aria-label="Enviar mensaje"
                  onClick={handleSend}
                  disabled={!inputValue.trim() || isLoading}
                  className="absolute right-1.5 p-2 rounded-full bg-cyan-600 text-white hover:bg-cyan-700 disabled:opacity-50 disabled:hover:bg-cyan-600 transition-colors"
                >
                  <Send className="w-4 h-4" />
                </button>
              </div>
              <div className="mt-2 text-center">
                <span className="text-[10px] text-slate-400 dark:text-slate-500">
                  La IA puede cometer errores. Verifica tus saldos.
                </span>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Floating Button */}
      <AnimatePresence>
        {!isOpen && (
          <motion.button
            type="button"
            aria-label="Abrir chat"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            exit={{ scale: 0 }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setIsOpen(true)}
            className="relative w-16 h-16 rounded-full shadow-2xl flex items-center justify-center focus:outline-none focus:ring-4 focus:ring-cyan-500/50 overflow-visible"
          >
            {/* Glowing background effect */}
            <div className="absolute inset-0 rounded-full bg-gradient-to-br from-cyan-400 to-cyan-600 blur-sm opacity-50 animate-pulse"></div>
            
            {/* The icon itself, rendered over the background */}
            <div className="relative z-10 w-full h-full rounded-full overflow-hidden bg-white shadow-inner p-1">
              <img 
                src="/icono-chat.png" 
                alt="Chat Bot" 
                className="w-full h-full object-contain"
              />
            </div>
          </motion.button>
        )}
      </AnimatePresence>
    </div>
  );
};

export default FloatingAIChat;
